export const emailRegexp = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
export type Question = {
  text: string
  subheading?: string
  if?: (answers: Record<string, string>) => boolean
  optional?: true
  validator?: (answer: string) => boolean
  description?: string
  attrs?: Record<string, string | Record<string, string>>
  width?: string
} & (
  | {
      type: 'radio'
      options: string[]
      default?: string
      innerWidth?: string
    }
  | {
      type: 'checkboxes'
      options: string[]
      innerWidth?: string
    }
  | {
      type:
        | 'email'
        | 'text'
        | 'tel'
        | 'textarea'
        | 'number'
        | 'date'
        | 'datetime'
    }
)

export type Category = {
  title: string
  description?: string
  if?: (answers: Record<string, string>) => boolean
  questions: Record<string, Question>
}

const address: (
  title: string,
  id: string,
  callback?: (question: Question) => Question,
) => Record<string, Question> = (title, id, callback = q => q) => ({
  [`${id}Street`]: callback({
    subheading: title,
    type: 'text',
    text: `Street name and number`,
    validator: answer => !!answer,
  }),
  [`${id}Town`]: callback({
    type: 'text',
    text: `Town`,
    validator: answer => !!answer,
    width: 'calc(60% - 6.5rem)',
    attrs: {
      autocapitalize: 'words',
    },
  }),
  [`${id}Postcode`]: callback({
    type: 'text',
    text: `Postcode`,
    validator: answer => !!answer,
    attrs: {
      autocapitalize: 'characters',
      style: {
        textTransform: 'uppercase',
      },
    },
    width: '12rem',
  }),
  [`${id}Country`]: callback({
    type: 'text',
    text: `Country`,
    validator: answer => !!answer,
    width: 'calc(40% - 6.5rem)',
    attrs: {
      autocapitalize: 'words',
    },
  }),
})

const childQuestions: (id: number) => Record<string, Question> = (
  id: number,
) => ({
  [`child${id}Name`]: {
    subtitle: `Child ${id}`,
    type: 'text',
    text: 'Name',
    attrs: {
      autocapitalize: 'words',
    },
    if: answers => answers.type === 'Family' && Number(answers.children) >= id,
  },
  [`child${id}Birth`]: {
    type: 'date',
    text: 'Date of birth',
    if: answers => answers.type === 'Family' && Number(answers.children) >= id,
  },
  [`child${id}Health`]: {
    type: 'textarea',
    text: 'Any health, dietary, physical or medical requirements',
    if: answers => answers.type === 'Family' && Number(answers.children) >= id,
  },
})

export const questions: Record<string, Category> = {
  introduction: {
    title: 'Introduction',
    description: `<p>Thank you for applying to serve in Hungary in 2020.</p>
    <p>Your details will be saved as you fill out the form, so you can take your time in filling it out.</p>`,
    questions: {
      faith: {
        description: `All applicants have to first read and agree to <a href="http://www.acorncamps.co.uk/about-us/what-we-believe/">the Statement of Faith</a>. If you have any questions about this please contact us.`,
        text: `I agree with the Statement of Faith`,
        type: 'radio',
        options: ['Yes', 'No'],
        validator: answer => answer === 'Yes',
        attrs: {
          pattern: 'Yes',
          title: 'All applicants must agree to the Statement of Faith.',
        },
      },
      camps: {
        text: 'What camps are you applying for?',
        type: 'radio',
        options: ['Summer', 'Other'],
        validator: answer => !!answer,
      },
      particularCamp: {
        text: 'Which camps are you applying for?',
        type: 'checkboxes',
        if: answers => answers.camps === 'Summer',
        options: [
          // 'Külső kelenföld',
          'Sarokkő',
          'Kelenföld',
          'Tahitótfalu', // Andy
          'Orosháza',
          'Gyula',
          'Vác', // Szilard
          'Szarvas', // Heida
          // 'Kokava',
          `I'd be happy with any camp`,
        ],
      },
      involvement: {
        text: `How do you picture your involvement with ACORN this summer?`,
        type: 'radio',
        if: answers => answers.camps === 'Summer',
        options: [
          `So far I’m coming as a one-off`,
          `Part of a 3-year commitment with ACORN`,
          `I’m involved long-term with ACORN`,
        ],
      },
      type: {
        text: `I’m applying to come as a...`,
        type: 'radio',
        options: ['Single person', 'Married couple', 'Family'],
        validator: answer => !!answer,
      },
      children: {
        description: `Each adult will need to fill out an application form. On one form please let us know about the children who you wish to bring on the camp. List their names and dates of birth, together with any relevant health, dietary, physical, medical requirements.`,
        text: 'How many children do you have?',
        type: 'number',
        attrs: {
          min: '1',
        },
        if: answers => answers.type === 'Family',
      },
      ...childQuestions(1),
      ...childQuestions(2),
      ...childQuestions(3),
      ...childQuestions(4),
      ...childQuestions(5),
    },
  },
  planningWeekend: {
    title: 'Team planning',
    description: `This will be team-based for 2020, and your team leader will arrange this, aiming to take the team’s availability into account. It is compulsory for all volunteers.`,
    questions: {
      planningMeetingAttendance: {
        text: `I commit to prioritising attendance at my team’s camp planning meetings, as part of my service on this mission`,
        type: 'radio',
        options: ['Yes', 'No'],
        validator: answer => answer === 'Yes',
      },
      tShirt: {
        text: `Do you have an ACORN t-shirt?`,
        type: 'radio',
        options: ['Yes', 'No'],
      },
      tShirtSize: {
        text: 'What size are you?',
        type: 'radio',
        if: answers => answers.tShirt === 'No',
        options: ['S', 'M', 'L', 'XL'],
      },
    },
  },
  background: {
    title: 'About you',
    description: `Please check that <router-link to="/profile/details">your profile</router-link> is up-to-date.`,
    questions: {
      profileUpdated: {
        text: `I've checked and updated my profile.`,
        type: 'radio',
        options: ['Yes', 'No'],
        validator: answer => answer === 'Yes',
        attrs: {
          pattern: 'Yes',
          title: 'You must check and update your profile.',
        },
      },
      why: {
        text: `Why are you applying to come to Hungary?`,
        type: 'textarea',
      },
      experience: {
        text: `If coming as a mentor or speaker, please outline your experience`,
        type: 'textarea',
      },
    },
  },
  travel: {
    title: 'Travel arrangements',
    description: `ACORN can book flights for you even if you have particular travel requirements.`,
    if: () => new Date().getTime() < new Date(2020, 0, 1).getTime(),
    questions: {
      bookingTravel: {
        text: `Would you like us to book flights for you?`,
        type: 'radio',
        options: ['Yes', 'No'],
      },
      travelInfo: {
        text: `Do you have any specific requirements or other travel information?`,
        if: answers => answers.bookingTravel === 'Yes',
        type: 'textarea',
      },
    },
  },
  referrees: {
    title: 'Your referees',
    description: `<p>ACORN will contact your referees on your behalf.</p>`,
    questions: {
      referree1Name: {
        subheading: 'Church referee',
        text: `Name`,
        type: 'text',
        attrs: {
          autocapitalize: 'words',
        },
        validator: answer => !!answer,
      },
      referree1Church: {
        text: `church`,
        type: 'text',
        validator: answer => !!answer,
        attrs: {
          autocapitalize: 'words',
        },
        width: '55%',
      },
      referree1Position: {
        text: `Position in church`,
        type: 'text',
        validator: answer => !!answer,
        width: 'calc(45% - 0.5rem)',
      },
      referree1Email: {
        text: `Email`,
        type: 'email',
        width: 'calc(50% - 0.25rem)',
        validator: answer => emailRegexp.test(answer),
      },
      referree1Phone: {
        text: `Telephone`,
        type: 'tel',
        width: 'calc(50% - 0.25rem)',
        validator: answer => !!answer,
      },
      referree2Name: {
        subheading: 'Personal referee',
        text: `Name`,
        type: 'text',
        attrs: {
          autocapitalize: 'words',
        },
        if: answers =>
          !answers.pastAcornExperienceYear ||
          new Date().getFullYear() - Number(answers.pastAcornExperienceYear) >=
            3,
        validator: answer => !!answer,
      },
      referree2Occupation: {
        text: `Occupation`,
        type: 'text',
        width: '60%',
        attrs: {
          autocapitalize: 'words',
        },
        if: answers =>
          !answers.pastAcornExperienceYear ||
          new Date().getFullYear() - Number(answers.pastAcornExperienceYear) >=
            3,
        validator: answer => !!answer,
      },
      referree2YearsKnown: {
        text: `Number of years known`,
        type: 'number',
        width: 'calc(40% - 0.5rem)',
        if: answers =>
          !answers.pastAcornExperienceYear ||
          new Date().getFullYear() - Number(answers.pastAcornExperienceYear) >=
            3,
        validator: answer => !!answer,
        attrs: {
          min: '0',
        },
      },
      referree2Email: {
        text: `Email`,
        type: 'email',
        width: '50%',
        if: answers =>
          !answers.pastAcornExperienceYear ||
          new Date().getFullYear() - Number(answers.pastAcornExperienceYear) >=
            3,
        validator: answer => emailRegexp.test(answer),
      },
      referree2Phone: {
        text: `Telephone`,
        type: 'tel',
        width: 'calc(50% - 0.5rem)',
        if: answers =>
          !answers.pastAcornExperienceYear ||
          new Date().getFullYear() - Number(answers.pastAcornExperienceYear) >=
            3,
        validator: answer => !!answer,
      },
    },
  },
  additional: {
    title: 'Final details',
    questions: {
      additionalConsents: {
        text: `Please note: if you do <b>NOT</b> check a box below, you indicate that you do <b>NOT</b> consent.`,
        type: 'checkboxes',
        options: [
          'I confirm that I have read and fully understand the role description I have applied for.',
          'I agree that as a volunteer my primary concern is to serve with my team to the benefit of ACORN and the local church.',
          'I declare that to the best of my knowledge, the information I have given in this form is true and accurate.',
          'I consent to further checks being made with the relevant authorities as necessary.',
          'I am happy to receive the ACORN newsletter twice a year, and occasional information about upcoming events.',
        ],
        innerWidth: '100%',
      },
    },
  },
}

export const dataConsent: Record<string, Category> = {
  required: {
    title: 'Welcome!',
    description: `
      <p>
        <strong>First, we'll need your consent to store your information.</strong> We ask for this now because we'll store all your answers as you type, so you can close the window and come back to your application at any point.
      </p>
      <p>
        <strong>Then, complete your profile.</strong> You'll only need to do this
        once, and it will be saved to save you time in future years! You'll
        need to fully complete it before you can begin your application.
      </p>
      <p>
        <strong>Finally, complete and submit the ACORN Application Form.</strong>
      </p>`,
    questions: {
      storeDetails: {
        description: `Your contact information is stored on a secured database accessible only to ACORN staff and your team leader. We do not pass on any information to third-party companies or individuals. I consent to ACORN using my information as outlined in the <a href="http://www.acorncamps.co.uk/about-us/privacy-notice/">ACORN Privacy Notice</a>, where you can also change your privacy settings.`,
        text: 'I am happy for ACORN to store my details',
        type: 'radio',
        options: ['Yes', 'No'],
        validator: answer => !!answer,
      },
    },
  },
}

export const profile: Record<string, Category> = {
  personal: {
    title: 'Personal details',
    questions: {
      title: {
        text: `Title`,
        type: 'text',
        width: '5rem',
        attrs: {
          autocapitalize: 'words',
        },
      },
      firstName: {
        text: `First name (as on passport)`,
        type: 'text',
        width: 'calc(50% - 3rem)',
        attrs: {
          autocapitalize: 'words',
        },
        validator: answer => !!answer,
      },
      surname: {
        text: `Surname`,
        type: 'text',
        width: 'calc(50% - 3rem)',
        attrs: {
          autocapitalize: 'words',
        },
        validator: answer => !!answer,
      },
      dob: {
        text: `Date of birth`,
        type: 'date',
        width: 'calc(50% - 0.25rem)',
        validator: answer => !!answer,
      },
      previousName: {
        text: `Previous/maiden name`,
        type: 'text',
        attrs: {
          autocapitalize: 'words',
        },
        width: 'calc(50% - 0.25rem)',
      },
      gender: {
        text: `Gender`,
        type: 'radio',
        options: ['Male', 'Female'],
        width: 'calc(60% - 0.25rem)',
        validator: answer => !!answer,
      },
      student: {
        text: `Are you a student?`,
        type: 'radio',
        options: ['No', 'Yes'],
        width: 'calc(40% - 0.25rem)',
        validator: answer => !!answer,
      },
      nationality: {
        text: `Nationality`,
        attrs: {
          autocapitalize: 'words',
        },
        type: 'text',
      },
    },
  },
  contact: {
    title: 'Contact details',
    questions: {
      email: {
        text: `Email address`,
        type: 'email',
        width: 'calc(50% - 0.5rem)',
        validator: answer => emailRegexp.test(answer),
      },
      mobile: {
        text: `Mobile Number`,
        type: 'tel',
        width: 'calc(50% - 0.5rem)',
      },
      ...address('Home address', 'home'),
      ...address('Term-time address', 'term', field => ({
        ...field,
        if: answers => answers.student === 'Yes',
      })),
    },
  },
  backgroundInfo: {
    title: 'More about you',
    questions: {
      church: {
        text: `Your church`,
        type: 'text',
        width: 'calc(50% - 0.25rem)',
        validator: answer => answer.length > 4,
        attrs: {
          autocapitalize: 'words',
          pattern: '.{5,}',
          title: 'Five or more characters',
        },
      },
      timeAtChurch: {
        text: `How many years have you been there?`,
        type: 'text',
        width: 'calc(50% - 0.25rem)',
      },
      christianStory: {
        text: `What is your Christian story?`,
        type: 'textarea',
      },
      teamWorkExperience: {
        text: `Your experience with teamwork`,
        type: 'textarea',
      },
      youthWorkExperience: {
        text: `Your experience with youth-work`,
        type: 'textarea',
      },
    },
  },
  acornBackground: {
    title: 'Acorn experience',
    questions: {
      hearAboutAcorn: {
        text: `How did you hear about ACORN?`,
        type: 'textarea',
      },
      pastAcornExperience: {
        text: `Have you helped out with ACORN before?`,
        type: 'radio',
        options: ['Yes', 'No'],
        validator: answer => !!answer,
      },
      pastAcornExperienceYear: {
        text: `When did you last serve with ACORN?`,
        type: 'number',
        if: answers => answers.pastAcornExperience === 'Yes',
        attrs: {
          min: '1992',
          max: String(new Date().getFullYear()),
        },
        validator: answer =>
          Number.isInteger(Number(answer)) &&
          Number(answer) > 1990 &&
          Number(answer) <= new Date().getFullYear(),
      },
      pastAcornChurches: {
        text: `Which churches have you served with?`,
        type: 'checkboxes',
        options: [
          'Tahitótfalu',
          'Székesfehérvár',
          'Vác',
          'Orosháza',
          'Szarvas',
          'Nagybajom',
          'Kokava',
          'Farnad',
          'Kelenföld',
          'Budaörs',
          'Siklos',
          'Kecskemét',
          'Gödölö',
          'Miskolc',
          'Winter Camp',
          '2019 - The Big Meet',
          'Other',
        ],
        if: answers => answers.pastAcornExperience === 'Yes',
      },
    },
  },
  dbs: {
    title: 'Child protection',
    questions: {
      dbsCertificate: {
        text: `Do you hold a DBS certificate for ACORN?`,
        type: 'radio',
        options: ['No', 'Yes'],
        validator: answer => !!answer,
      },
      dbsExpiration: {
        text: `When was your DBS certificate issued?`,
        type: 'date',
        if: answers => answers.dbsCertificate === 'Yes',
        validator: answer =>
          !!answer && new Date(answer).getTime() < new Date().getTime(),
      },
      childProtectionDeclaration: {
        type: 'radio',
        options: ['Yes', 'No'],
        validator: answer => answer === 'Yes',
        description: `ACORN require all applicants and volunteers to make a self-declaration relating to
          criminal convictions, which can be downloaded <a href="/2020 Acorn Self-Declaration Form.docx">here</a>.`,
        text:
          'I have completed and posted this form to the ACORN Child Protection Officer.',
      },
    },
  },
  travelDetails2020: {
    title: `Outward travel details - 2020`,
    if: answers => answers.bookingTravel !== 'Yes',
    description: `If you've not asked us to book travel for you, please let us know your details.`,
    questions: {
      airline2020: {
        text: 'Airline',
        type: 'text',
        width: 'calc(50% - 6.25rem)',
        attrs: {
          autocapitalize: 'words',
        },
      },
      bookingReference2020: {
        text: 'Booking Reference',
        type: 'text',
        width: 'calc(50% - 6.25rem)',
      },
      baggageAllowance2020: {
        text: 'Baggage (kgs)',
        type: 'number',
        width: 'calc(12rem)',
        attrs: { min: '0' },
      },
      airportDeparture2020: {
        text: 'Departure Airport',
        type: 'text',
        width: 'calc(33% - 0.25rem)',
        attrs: {
          autocapitalize: 'words',
        },
      },
      airportDepartureTerminal2020: {
        text: 'Terminal',
        type: 'text',
        width: 'calc(33% - 0.25rem)',
        attrs: {
          autocapitalize: 'words',
        },
      },
      airportDepartureTime2020: {
        text: 'Departure date',
        type: 'datetime',
        width: 'calc(33% - 0.25rem)',
      },
      airportArrival2020: {
        text: 'Arrival Airport',
        type: 'text',
        width: 'calc(33% - 0.25rem)',
        attrs: {
          autocapitalize: 'words',
        },
      },
      airportArrivalTerminal2020: {
        text: 'Terminal',
        type: 'text',
        width: 'calc(33% - 0.25rem)',
        attrs: {
          autocapitalize: 'words',
        },
      },
      airportArrivalTime2020: {
        text: 'Arrival time',
        type: 'text',
        width: 'calc(33% - 0.25rem)',
      },
    },
  },
  travelDetailsReturn2020: {
    title: `Return travel details - 2020`,
    if: answers => answers.bookingTravel !== 'Yes',
    questions: {
      returnAirline2020: {
        text: 'Airline',
        type: 'text',
        width: 'calc(33% - 0.25rem)',
        attrs: {
          autocapitalize: 'words',
        },
      },
      returnBookingReference2020: {
        text: 'Booking Reference',
        type: 'text',
        width: 'calc(33% - 0.25rem)',
      },
      returnAirportDeparture2020: {
        text: 'Departure Airport',
        type: 'text',
        width: 'calc(33% - 0.25rem)',
        attrs: {
          autocapitalize: 'words',
        },
      },
      returnAirportDepartureTerminal2020: {
        text: 'Departure Terminal',
        type: 'text',
        width: 'calc(33% - 0.25rem)',
        attrs: {
          autocapitalize: 'words',
        },
      },
      returnAirportDepartureTime2020: {
        text: 'Departure time',
        type: 'text',
        width: 'calc(33% - 0.25rem)',
      },
      returnAirportArrival2020: {
        text: 'Arrival Airport',
        type: 'text',
        width: 'calc(33% - 0.25rem)',
        attrs: {
          autocapitalize: 'words',
        },
      },
    },
  },
  insurance: {
    title: 'Travel insurance',
    description: `You need to have up-to-date travel insurance.`,
    questions: {
      insuranceProvider: {
        text: 'Provider',
        type: 'text',
        width: 'calc(50% - 0.25rem)',
        attrs: {
          autocapitalize: 'words',
        },
      },
      insurancePolicyNumber: {
        text: 'Policy number',
        type: 'text',
        attrs: {
          autocapitalize: 'characters',
        },
        width: 'calc(50% - 0.25rem)',
      },
      insurancePhoneNumber: {
        text: '24-hour phone number',
        type: 'tel',
      },
    },
  },
  ehic: {
    title: 'European Health Insurance (or TAJ) Card',
    description: `Find out more and apply <a href="https://www.nhs.uk/using-the-nhs/healthcare-abroad/apply-for-a-free-ehic-european-health-insurance-card/">on the NHS website</a>.`,
    questions: {
      ehicCardNumber: {
        text: `EHIC/TAJ Card Number (20 characters)`,
        type: 'text',
        width: 'calc(50% - 0.25rem)',
        // validator: answer => answer.length === 20,
        attrs: {
          // pattern: `[A-Za-z0-9]{20}`,
          autocapitalize: 'characters',
          title: '20 characters (A-Z, 0-9)',
          placeholder: 'EXAMPLE0123456789012',
        },
      },
      ehicExpiryDate: {
        text: `EHIC/TAJ Expiry Date`,
        type: 'date',
        width: 'calc(50% - 0.25rem)',
        // validator: answer =>
        //   !!answer && new Date(answer).getTime() > new Date().getTime(),
      },
      ehicPersonalIdentification: {
        text: `EHIC/TAJ Personal Identification number (2 letters, 8 numbers)`,
        type: 'text',
        // validator: answer => /^\w{2}\d{8}$/.test(answer),
        attrs: {
          // pattern: `[A-Za-z]{2}[0-9]{8}`,
          placeholder: 'AB12345678',
          autocapitalize: 'characters',
          title: '2 letters (A-Z) then 8 numbers (0-9)',
        },
      },
    },
  },
  health: {
    title: 'Your health',
    questions: {
      dietaryRequirements: {
        text: 'Any dietary requirements?',
        type: 'text',
      },
      medicalTreatment: {
        text: `Are you currently receiving any treatment for any medical conditions?`,
        type: 'radio',
        options: [`No`, 'Yes'],
        validator: answer => !!answer,
      },
      healthIssues: {
        text: `Do you have any health problems we should know about, including disabilities, treatments, allergies, food intolerances, difficulties sleeping etc?`,
        type: 'radio',
        options: [`No`, 'Yes'],
        validator: answer => !!answer,
      },
      mentalHealth: {
        text: `Have you ever suffered from any mental or emotional health problems?`,
        type: 'radio',
        options: [`No`, 'Yes'],
        validator: answer => !!answer,
      },
      physicalHealth: {
        text: `Have you any physical impairment preventing you from carrying out strenuous physical tasks?`,
        type: 'radio',
        options: [`No`, 'Yes'],
        validator: answer => !!answer,
      },
      healthMoreInformation: {
        text: `Please describe`,
        type: 'textarea',
        if: answers =>
          answers.medicalTreatment === 'Yes' ||
          answers.healthIssues === 'Yes' ||
          answers.mentalHealth === 'Yes' ||
          answers.physicalHealth === 'Yes',
        validator: answer => answer.length > 5,
        attrs: {
          pattern: '.{6,}',
          title: 'Six or more characters',
        },
      },
    },
  },
  emergencyContact1: {
    title: 'Emergency Contact 1',
    questions: {
      contact1Name: {
        text: `Name`,
        type: 'text',
        validator: answer => answer.length > 5,
        attrs: {
          autocapitalize: 'words',
          pattern: '.{6,}',
          title: 'Six or more characters',
        },
      },
      contact1Telephone: {
        text: `Telephone`,
        type: 'tel',
        width: 'calc(50% - 0.25rem)',
        validator: answer => answer.length > 5,
        attrs: {
          pattern: '.{6,}',
          title: 'Six or more characters',
        },
      },
      contact1Relationship: {
        text: `Relationship`,
        type: 'text',
        width: 'calc(50% - 0.25rem)',
        attrs: {
          autocapitalize: 'words',
        },
        validator: answer => !!answer,
      },
    },
  },
  emergencyContact2: {
    title: 'Emergency Contact 2',
    questions: {
      contact2Name: {
        text: `Name`,
        type: 'text',
        validator: answer => answer.length > 5,
        attrs: {
          pattern: '.{6,}',
          autocapitalize: 'words',
          title: 'Six or more characters',
        },
      },
      contact2Telephone: {
        text: `Telephone`,
        type: 'tel',
        width: 'calc(50% - 0.25rem)',
        validator: answer => answer.length > 5,
        attrs: {
          pattern: '.{6,}',
          title: 'Six or more characters',
        },
      },
      contact2Relationship: {
        text: `Relationship`,
        type: 'text',
        width: 'calc(50% - 0.25rem)',
        attrs: {
          autocapitalize: 'words',
        },
        validator: answer => !!answer,
      },
    },
  },
}

export const referenceReturning: Record<string, Category> = {
  refereeDetails: {
    title: 'Contact details',
    questions: {
      refereeName: {
        text: 'Your name',
        type: 'text',
        validator: answer => !!answer,
      },
      refereeChurch: {
        text: 'Your church',
        type: 'text',
        width: 'calc(50% - 0.25rem)',
        validator: answer => !!answer,
      },
      refereeChurchPosition: {
        text: 'Position in church',
        type: 'text',
        width: 'calc(50% - 0.25rem)',
        validator: answer => !!answer,
      },
      ...address('Address', 'home'),
      refereeWorkPhone: {
        text: 'Work phone number',
        type: 'tel',
        width: 'calc(50% - 0.25rem)',
        validator: answer => !!answer,
      },
      refereeHomePhone: {
        text: 'Home phone number',
        type: 'tel',
        width: 'calc(50% - 0.25rem)',
      },
      refereeEmail: {
        text: 'Email address',
        type: 'email',
        validator: answer => !!answer,
      },
    },
  },
  applicantDetails: {
    title: 'Applicant details',
    questions: {
      applicantName: {
        text: 'Name of applicant',
        type: 'text',
        validator: answer => !!answer,
      },
      applicantKnowledge: {
        text: 'How long have you known the applicant? In what capacity?',
        type: 'textarea',
        validator: answer => !!answer,
      },
      suitability: {
        description: `Please read <a href="http://www.acorncamps.co.uk/uk-volunteers/application-process/">this information for referees</a>.`,
        text:
          'Has anything changed over the past year, or since the applicant last served with ACORN that would cause you to question their suitability to serve on a camp this year?',
        type: 'radio',
        options: ['Yes', 'No'],
        validator: answer => !!answer,
      },
      whyNotSuitable: {
        text: 'Please give details.',
        type: 'textarea',
        if: answers => answers.suitability === 'Yes',
        validator: answer => !!answer,
      },
      declaration: {
        text: 'Declaration',
        options: [
          'To the best of my knowledge, the information I have given on this form is current and accurate.',
        ],
        type: 'checkboxes',
        innerWidth: '100%',
        validator: answer => !!answer.length,
      },
      date: {
        text: 'Date',
        type: 'date',
        validator: answer => !!answer,
      },
      moreInformation: {
        description:
          'The newsletter is sent out twice a year, to give information to supporters and fuel for prayer',
        text: 'I would like to find out more about ACORNCAMPS',
        type: 'radio',
        options: ['Yes', 'No', `I'm already receiving the newsletter`],
        validator: answer => !!answer,
      },
    },
  },
}

type QuestionAnswers<T extends Record<string, Question | Category>> = {
  [P in keyof T]: T[P] extends Question
    ? string
    : {
        [Q in keyof T[P]]: string
      }
}

export type ProfileAnswers = QuestionAnswers<typeof profile>

export const getAnswersObject = (questions: Record<string, Category>) =>
  Object.keys(questions).reduce((answerKeys, category) => {
    Object.keys(questions[category].questions).forEach(
      key => (answerKeys[key] = ''),
    )
    return answerKeys
  }, {} as Record<string, string>)

export const flattenQuestionsObject = (questions: Record<string, Category>) =>
  Object.keys(questions).reduce((questionObject, category) => {
    Object.keys(questions[category].questions).forEach(
      key => (questionObject[key] = questions[category].questions[key]),
    )
    return questionObject
  }, {} as Record<string, Question>)

export type ArrayObject<T> = T & { key: string }
export const toArray = <T>(
  objectToConvert: Record<string, T>,
): Array<ArrayObject<T>> =>
  Object.keys(objectToConvert).map(key => ({
    ...objectToConvert[key],
    key,
  }))

export const filterConditional = <
  T extends { if?: (answers: Record<string, string>) => boolean }
>(
  item: T,
  answers: Record<string, string> = {},
) => {
  return !item.if || item.if(answers)
}

export const flattenAndFilterQuestions = (
  questions: Record<string, Category>,
  answers: Record<string, string> = {},
) => {
  const activeCategories = toArray(questions).filter(category =>
    filterConditional(category, answers),
  )
  const questionArray: ArrayObject<Question>[] = []
  activeCategories.forEach(category => {
    toArray(category.questions).forEach(question => {
      if (filterConditional(question, answers)) questionArray.push(question)
    })
  })
  return questionArray
}

export const getPercentComplete = (
  questions: Record<string, Category>,
  answers: Record<string, string>,
) => {
  const questionsArray = flattenAndFilterQuestions(questions, answers)
  let numberOfQuestions = questionsArray.length
  let numberOfQuestionsCompleted = questionsArray.filter(
    question =>
      !question.validator || question.validator(answers[question.key] || ''),
  ).length

  return Math.floor((100 * numberOfQuestionsCompleted) / numberOfQuestions)
}
